import { Signal } from '@angular/core';
import { AbstractControl, Validators } from '@angular/forms';
import { QaroniBaseFormsDirective } from '@qaroni-core/directives/qaroni-base-forms/qaroni-base-forms.directive';
import { DurationInputValue } from './duration-input';

export abstract class DurationInputForm extends QaroniBaseFormsDirective {
  protected abstract readonly durationMinutes: Signal<boolean>;

  protected readonly maxHours = 24;

  private skeleton = {
    hours: [
      null,
      [Validators.min(0), Validators.max(this.maxHours), Validators.required],
    ],
    minutes: [
      null,
      [Validators.min(0), Validators.max(59), Validators.required],
    ],
  };

  public form = this.fb.group(this.skeleton);

  public get hours(): AbstractControl {
    return this.form.get('hours');
  }

  public get minutes(): AbstractControl {
    return this.form.get('minutes');
  }

  protected isAtMaxHours(hours: number | null): boolean {
    return hours !== null && hours >= this.maxHours;
  }

  protected syncMinutesLock(isAtMax: boolean): void {
    if (isAtMax && this.minutes.enabled) {
      this.minutes.disable({ emitEvent: false });
    } else if (!isAtMax && this.minutes.disabled) {
      this.minutes.enable({ emitEvent: false });
    }
  }

  protected populate(value: number | null): void {
    const parsed = this.fromModelValue(value);
    const normalized = this.computeNormalized(parsed.hours, parsed.minutes);

    this.form.patchValue(normalized, { emitEvent: false });
    this.syncMinutesLock(this.isAtMaxHours(normalized.hours));
  }

  protected prepareFormToSend(): number | null {
    const rawHours = this.hours.value;
    const rawMinutes = this.minutes.value;
    const normalized = this.computeNormalized(rawHours, rawMinutes);

    this.hours.setValue(normalized.hours, { emitEvent: false });
    this.minutes.setValue(normalized.minutes, { emitEvent: false });

    this.syncMinutesLock(this.isAtMaxHours(normalized.hours));

    return this.toModelValue(normalized.hours, normalized.minutes);
  }

  private toModelValue(hours: number, minutes: number): number | null {
    if (hours === null && minutes === null) {
      return null;
    }

    const h = hours ?? 0;
    const m = minutes ?? 0;

    if (this.durationMinutes()) {
      return h * 60 + m;
    }

    return Math.round((h + m / 60) * 100) / 100;
  }

  private fromModelValue(value: number | null): DurationInputValue {
    if (value === null || value === undefined) {
      return { hours: null, minutes: null };
    }

    if (this.durationMinutes()) {
      const totalMinutes = Math.round(value);

      return {
        hours: Math.floor(totalMinutes / 60),
        minutes: totalMinutes % 60,
      };
    }

    let hours = Math.floor(value);
    let minutes = Math.round((value - hours) * 60);

    if (minutes === 60) {
      hours += 1;
      minutes = 0;
    }

    return { hours, minutes };
  }

  private clamp(value: number | null, min: number, max: number): number | null {
    if (value === null || value === undefined) {
      return null;
    }

    return Math.min(Math.max(value, min), max);
  }

  private computeNormalized(
    hours: number | null,
    minutes: number | null
  ): DurationInputValue {
    const clampedHours = this.clamp(hours, 0, this.maxHours);
    const isAtMax = this.isAtMaxHours(clampedHours);
    const clampedMinutes = isAtMax ? 0 : this.clamp(minutes, 0, 59);

    return { hours: clampedHours, minutes: clampedMinutes };
  }
}
